import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { CircleAlertIcon, RefreshCwIcon } from 'lucide-react';
import { api } from '#web/api';
import { AuthTransitionPage } from '#web/auth/AuthTransitionPage';
import { ViewerProvider } from '#web/auth/viewer';
import { AppShell } from '#web/components/AppShell';
import { Button } from '#web/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '#web/components/ui/card';

/**
 * Pathless layout for every signed-in page: checks the GitHub session (redirecting to /login when
 * there is none), then renders the app shell. `_app` adds no URL segment.
 */
export const Route = createFileRoute('/_app')({
  component: AppLayout,
});

function AppLayout() {
  const me = api.auth.me.useQuery(undefined, { retry: false });

  if (me.isPending) {
    return <AuthTransitionPage title="Checking your session" description="Confirming your GitHub sign-in…" />;
  }
  if (me.error) {
    return (
      <main className="flex min-h-dvh items-center justify-center p-6">
        <Card className="w-full max-w-sm -translate-y-[5vh]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CircleAlertIcon className="size-4 text-destructive" />
              Couldn't check your session
            </CardTitle>
            <CardDescription>{me.error.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" size="sm" variant="outline" onClick={() => void me.refetch()}>
              <RefreshCwIcon /> Try again
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;

  return (
    <ViewerProvider viewer={me.data}>
      <AppShell>
        <Outlet />
      </AppShell>
    </ViewerProvider>
  );
}
